import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SetStatus from "../../../Components/SetStatus/SetStatus";
import { setSelectedInvoice } from "../../../Reducer/SelectedInvoiceReducer";
import { setSelectedInvoiceData } from "../../../Reducer/SelectedInvoiceDataReducer";
import CommonTable from "../../../Components/CustomTable/CustomTable";
import { GetInvoicesService_NEW, DeleteInvoice } from "../../../Services/InvoiceService/InvoiceService";
import { SupplierStatementArchiveRecord } from "../../../Types/SupplierStatementArchive";
import Loader from "../../../Components/Loader/Loader";
import { StatementView } from "../../../Components/StatementViewer";
import SupplierStatementFilter from "../../../Components/SupplierStatementFilter/SupplierStatementFilter";
import AdvancedSearch from "../../../Components/InvoiceSearch/AdvancedSearch";
import { FormValues, toInvoiceSearchPayload } from "../../../Components/InvoiceSearch/AdvanceType";
import { FiSearch,FiChevronDown } from "react-icons/fi";
import DeleteButton from "../../../Components/StatementViewer/StatementHeader/DeleteButton/DeleteButton";
import { TbFilter } from "react-icons/tb";
import ConfirmPopup from "../../../Components/ConfirmPopup/ConfirmPopup";
import AlertMessage from "../../../Components/AlertMessage/AlertMessage";
import { UpdateInvoiceStageAndArchiveStatusService } from "../../../Services/InvoiceService/InvoiceService";
import "./SupplierStatementArchive.css";
import "./Archieve.css";

const ARCHIVE_STATUSES = ["Archived", "Reconciled"];
const FETCH_LIMIT = 100;

interface SupplierStatementArchiveRow {
  id: number;
  stageId: number;
  archiveStatus: string;
  file: string;
  supplier: string;
  date: string;
  outstandingBalance: string;
  user: string;
  note: string;
  record: SupplierStatementArchiveRecord;
}

const SupplierStatementArchive: React.FC = () => {
  const dispatch = useDispatch();
  const selectedClient = useSelector((state: any) => state.selectedClient.selectedClient);
  const userId = String(
    selectedClient?.userId ?? selectedClient?.clientId ?? selectedClient?.id ?? "",
  );
  const [records, setRecords] = useState<SupplierStatementArchiveRecord[]>([]);
  const [statement, setStatement] = useState<SupplierStatementArchiveRecord | null>(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [ssFilterStatus, setSsFilterStatus] = useState<string | null>(null);
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
  const [isOpenAdvance, setIsOpenAdvance] = useState(false);
  const [activeAdvancedSearch, setActiveAdvancedSearch] =
    useState<Partial<FormValues> | null>(null);
  const advancedSearchRef = useRef<{ resetForm: () => void }>(null);
  const [isUnarchiveConfirmOpen, setIsUnarchiveConfirmOpen] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadRecords = useCallback(async (searchFields = activeAdvancedSearch) => {
    if (!userId) {
      setRecords([]);
      setLoadError("Select a client to view its supplier statement archive.");
      setLoading(false);
      return;
    }

    const archiveStatuses =
      ssFilterStatus === "archive"
        ? ["Archived"]
        : ssFilterStatus === "reconciled"
          ? ["Reconciled"]
          : ARCHIVE_STATUSES;

    setLoading(true);
    try {
      const archivedInvoices: any[] = [];
      let after: string | null = null;
      let hasMore = true;

      while (hasMore) {
        const result: Awaited<ReturnType<typeof GetInvoicesService_NEW>> =
          await GetInvoicesService_NEW({
          userId,
          after,
          stageIds: [],
          archiveStatuses,
          limit: FETCH_LIMIT,
          searchFields: toInvoiceSearchPayload(searchFields),
          });
        archivedInvoices.push(...result.Items);
        hasMore = result.HasMore;
        if (!hasMore || !result.NextCursor || result.NextCursor === after) break;
        after = result.NextCursor;
      }

      const archiveRecords = archivedInvoices
        .filter((invoice) => archiveStatuses.includes(invoice.archiveStatus))
        .map((invoice): SupplierStatementArchiveRecord => {
          const lineItems = Array.isArray(invoice.lineItems) ? invoice.lineItems : [];
          return {
            invoiceId: Number(invoice.id ?? invoice.invoiceId),
            status: invoice.archiveStatus,
            file: String(invoice.fileName ?? invoice.file ?? ""),
            supplier: String(invoice.supplier ?? ""),
            date: String(
              invoice.statementPeriod || invoice.invoiceDate || "",
            ).slice(0, 10),
            outstandingBalance: Number(
              invoice.outstandingBalance ?? invoice.totalAmount ?? 0,
            ),
            user: String(invoice.documentOwner ?? invoice.submittedBy ?? ""),
            note: String(invoice.description ?? ""),
            previousInboxStatus: String(invoice.stageId ?? ""),
            documentReference: String(invoice.documentReference ?? ""),
            documentUrl: String(invoice.documentUrl ?? ""),
            stageId: Number(invoice.stageId ?? 21),
            currency: String(invoice.currency ?? "GBP"),
            lineItems: lineItems.map((item: any) => ({
              description: String(item.description ?? item.Description ?? ""),
              quantity: Number(item.quantity ?? item.Quantity ?? 0),
              amount: Number(item.amount ?? item.Amount ?? 0),
            })),
          };
        })
        .filter((record) => Number.isFinite(record.invoiceId) && record.invoiceId > 0);

      setRecords(archiveRecords);
      setSelectedRowKeys([]);
      setLoadError("");
    } catch (error) {
      console.error("Failed to load supplier statement archive:", error);
      setLoadError("Supplier statement archive could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [activeAdvancedSearch, ssFilterStatus, userId]);

  useEffect(() => {
    void loadRecords();
  }, [loadRecords]);

  const openStatement = (record: SupplierStatementArchiveRecord) => {
    dispatch(setSelectedInvoice({ id: record.invoiceId, stageId: record.stageId }));
    dispatch(
      setSelectedInvoiceData({
        ...record,
        userId,
        adminUserId: null,
        documentOwner: record.user,
        type: "Supplier Statement",
        invoiceDate: record.date,
        extractedPoNumber: null,
        dueDate: null,
        category: null,
        productOrService: null,
        customer: String(selectedClient?.name ?? ""),
        markAsRebillable: null,
        description: record.note,
        amount: record.outstandingBalance,
        totalAmount: record.outstandingBalance,
        outstandingBalance: record.outstandingBalance,
        taxAmount: 0,
        netAmount: record.outstandingBalance,
        payment: null,
        paid: null,
        paymentMethod: null,
        isActive: true,
        isDelete: false,
      }),
    );
    setStatement(record);
  };

  const rows: SupplierStatementArchiveRow[] = records.map((record) => ({
    id: record.invoiceId,
    status: <SetStatus stageId={record.stageId} archived={record.status} />,
    stageId: record.stageId,
    archiveStatus: record.status,
    file: record.file,
    supplier: record.supplier,
    date: record.date,
    outstandingBalance: new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: record.currency || "GBP",
    }).format(record.outstandingBalance),
    user: record.user,
    note: record.note || "—",
    record,
  }));

  const columns = [
    {
      key: "selected",
      title: "Select all",
      dataIndex: "",
      isCheckbox: true,
      showSortArrows: false,
    },
    { key: "status", title: "Status", dataIndex: "stageId", isStatus: true, showSortArrows: false },
    { key: "file", title: "File", dataIndex: "file" },
    { key: "supplier", title: "Supplier", dataIndex: "supplier" },
    { key: "date", title: "Date", dataIndex: "date", isDate: true },
    {
      key: "outstandingBalance",
      title: "Outstanding Balance",
      dataIndex: "outstandingBalance",
    },
    { key: "user", title: "User", dataIndex: "user" },
    { key: "note", title: "Note", dataIndex: "note" },
  ];

  const handleSsFilterApply = ({ status }: { status: string | null }) => {
    setSsFilterStatus(status);
    setIsFilterPopupOpen(false);
  };

  const handleAdvancedApply = (searchFields: Partial<FormValues> | null) => {
    setActiveAdvancedSearch(searchFields);
  };

  const handleRowSelect = (row: SupplierStatementArchiveRow, checked: boolean) => {
    setSelectedRowKeys((selected) =>
      checked
        ? [...selected, row.id]
        : selected.filter((id) => id !== row.id),
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedRowKeys(checked ? rows.map((row) => row.id) : []);
  };

  const handleUnarchiveSelected = async () => {
    if (isUnarchiving || selectedRowKeys.length === 0) return;

    const selectedRecords = records.filter((record) =>
      selectedRowKeys.includes(record.invoiceId),
    );
    setIsUnarchiving(true);
    try {
      const results = await Promise.all(
        selectedRecords.map((record) =>
          UpdateInvoiceStageAndArchiveStatusService({
            invoiceIds: [record.invoiceId],
            stageId: null,
            archiveStatus: "UnArchived",
          }),
        ),
      );
      const failedCount = results.filter((result) => !result.success).length;
      await loadRecords();
      setIsUnarchiveConfirmOpen(false);
      if (failedCount > 0) {
        AlertMessage({
          type: "error",
          text: `${failedCount} statement(s) could not be unarchived.`,
        });
        return;
      }

      AlertMessage({
        type: "success",
        text: `${selectedRecords.length} statement unarchived successfully.`,
      });
    } catch (error) {
      console.error("Failed to unarchive selected supplier statements:", error);
      await loadRecords();
      setIsUnarchiveConfirmOpen(false);
      AlertMessage({
        type: "error",
        text: "Failed to unarchive the selected statement(s). Please try again.",
      });
    } finally {
      setIsUnarchiving(false);
    }
  };

  const navigateStatement = (index: number) => {
    const nextRecord = records[index];
    if (!nextRecord) return false;
    dispatch(setSelectedInvoice({ id: nextRecord.invoiceId, stageId: nextRecord.stageId }));
    setStatement(nextRecord);
    return true;
  };
  const currentStatementIndex = statement
    ? records.findIndex((record) => record.invoiceId === statement.invoiceId)
    : -1;

  if (statement) {
    return (
      <StatementView
        setStatementView={(open) => {
          if (!open) {
            setStatement(null);
            void loadRecords();
          }
        }}
        refreshInvoices={loadRecords}
        initialStatement={statement}
        historyEntityType="SupplierStatement"
        navigation={{
          currentIndex: currentStatementIndex,
          totalCount: records.length,
          onPrevious: () => navigateStatement(currentStatementIndex - 1),
          onNext: () => navigateStatement(currentStatementIndex + 1),
        }}
      />
    );
  }

  return (
    <section className="supplier-statement-archive">
      <div className="ss-toolbar">
        <div className="ss-toolbar__left">
             <div style={{
               pointerEvents: selectedRowKeys.length === 0 ? "none" : "auto",
            opacity: selectedRowKeys.length === 0 ? 0.4 : 1,
          }}>
            <DeleteButton
              invoiceIds={selectedRowKeys}
              onSuccess={loadRecords}
            />
          </div>
          <button
            type="button"
            className="ss-action-btn"
            disabled={selectedRowKeys.length === 0 || isUnarchiving}
            onClick={() => setIsUnarchiveConfirmOpen(true)}
          >
            Unarchive
          </button>
        </div>
        <div className="ss-toolbar__right">
          <div className="ss-search-box">
            <FiSearch size={16} style={{ color: "#474646", flexShrink: 0 }} />
            <input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              className="advanced-btn"
              onClick={() => setIsOpenAdvance(true)}
            >
              Advanced...
              <FiChevronDown style={{ marginLeft: "4px" }} />
            </button>
          </div>
          <AdvancedSearch
            setIsOpenAdvance={isOpenAdvance}
            onClose={() => setIsOpenAdvance(false)}
            FetchInvoices={loadRecords}
            onApply={handleAdvancedApply}
            formRef={advancedSearchRef}
            activeSearchFields={activeAdvancedSearch}
            isSupplierStatement
          />
          <div style={{ position: "relative", flexShrink: 0 }}>
            <button
              type="button"
              className="ss-filter-btn"
              onClick={() => setIsFilterPopupOpen((o) => !o)}
              title="Filter"
            >
              <TbFilter size={18} />
            </button>
            <SupplierStatementFilter
              isOpen={isFilterPopupOpen}
              onClose={() => setIsFilterPopupOpen(false)}
              onApply={handleSsFilterApply}
              activeStatus={ssFilterStatus}
              options={[
                { label: "Archive", value: "archive" },
                { label: "Reconciled", value: "reconciled" },
              ]}
            />
          </div>
        </div>
      </div>

      <ConfirmPopup
        blur
        isOpen={isUnarchiveConfirmOpen}
        title="Unarchive Statements"
        message={`Are you sure you want to unarchive ${selectedRowKeys.length} selected statement(s)?`}
        confirmLabel={isUnarchiving ? "Processing..." : "Unarchive"}
        cancelLabel="Cancel"
        confirmColor="var(--color-brand)"
        onConfirm={handleUnarchiveSelected}
        onCancel={() => setIsUnarchiveConfirmOpen(false)}
        confirmDisabled={isUnarchiving}
      />

      {loading ? (
        <Loader />
      ) : loadError ? (
        <p className="supplier-statement-archive__empty">{loadError}</p>
      ) : rows.length === 0 ? (
        <h1 style={{ textAlign: "center", marginTop: "40px", color: "#888" }}>
          No supplier statements found for this client.
        </h1>
      ) : (
        <CommonTable
          columns={columns}
          data={rows}
          rowKey="id"
          selectedRowKeys={selectedRowKeys}
          onRowCheckboxChange={handleRowSelect}
          selectAll={rows.length > 0 && selectedRowKeys.length === rows.length}
          handleSelectAll={handleSelectAll}
          totalCount={rows.length}
          onRowClick={(row) => openStatement(row.record)}
        />
      )}
    </section>
  );
};

export default SupplierStatementArchive;
